import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Share2, Code2, History, Palette } from '../../components/icons';
import { rgbToHex } from '../../utils/colors';
import type { RGB } from '../../types';

interface ColorExplorerHeaderProps {
  color: RGB;
  colorHistory: string[];
  showShareTooltip: boolean;
  onShare: () => void;
  onExport: () => void;
  onRandomize: () => void;
  onHistorySelect: (hex: string) => void;
}

export default function ColorExplorerHeader({ color, colorHistory, showShareTooltip, onShare, onExport, onRandomize, onHistorySelect }: ColorExplorerHeaderProps) {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const historyButton = useRef<HTMLButtonElement>(null);
  const historyPanel = useRef<HTMLDivElement>(null);
  const themesHex = rgbToHex(color.r, color.g, color.b).slice(1);
  useEffect(() => {
    if (!isHistoryOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setIsHistoryOpen(false); historyButton.current?.focus(); }
    };
    const onPointer = (event: PointerEvent) => {
      if (!historyPanel.current?.contains(event.target as Node)) setIsHistoryOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onPointer); };
  }, [isHistoryOpen]);

  return (
    <div className="ui-panel mb-6 flex flex-wrap items-center justify-between gap-3 p-3 sm:p-4">
      <Link to={'/themes/' + themesHex} className="ui-button-secondary"><Palette size={18} aria-hidden="true" />Theme palettes</Link>
      <div className="flex flex-wrap items-center gap-1.5">
        {colorHistory.length > 0 && <div ref={historyPanel} className="relative" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setIsHistoryOpen(false); }}>
          <button ref={historyButton} onClick={() => setIsHistoryOpen(value => !value)} className="ui-icon-button" title="Color history" aria-label="Color history" aria-expanded={isHistoryOpen} aria-controls="color-history">
            <History size={18} aria-hidden="true" />
          </button>
          {isHistoryOpen && <div id="color-history" className="ui-panel absolute left-0 top-full z-30 mt-2 w-60 p-4 shadow-floating sm:left-auto sm:right-0">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-secondary">Recent colors</p>
            <div className="grid grid-cols-4 gap-2">{colorHistory.map(hex => <button key={hex} onClick={() => { onHistorySelect(hex); setIsHistoryOpen(false); historyButton.current?.focus(); }} className="h-11 rounded-control border border-surface-border" style={{ backgroundColor: '#' + hex }} title={'#' + hex} aria-label={'Select #' + hex} />)}</div>
          </div>}
        </div>}
        <button onClick={onRandomize} className="ui-icon-button" title="Randomize color" aria-label="Randomize color"><RefreshCw size={18} aria-hidden="true" /></button>
        <button onClick={onExport} className="ui-button-secondary"><Code2 size={18} aria-hidden="true" />Export</button>
        <div className="relative">
          <button onClick={onShare} className="ui-button-secondary"><Share2 size={18} aria-hidden="true" />Share</button>
          {showShareTooltip && <span role="status" className="absolute right-0 top-full z-30 mt-2 whitespace-nowrap rounded-control bg-ink px-3 py-2 text-xs text-white shadow-floating">Link copied!</span>}
        </div>
      </div>
    </div>
  );
}
